import { MaskedLine, FadeIn, SectionLabel } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { IMG, STATS } from "@/lib/data";

const VALUES = [
  ["01", "Speed is a strategy", "Markets move at midnight. So do we."],
  ["02", "Data before decoration", "Every creative call is backed by a number."],
  ["03", "Bold beats safe", "Forgettable is the only failure we fear."],
  ["04", "One team, whole journey", "From first impression to final invoice."],
];

const TEAM = [
  { name: "Suchit Khedekar", role: "Founder & Creative Head" },
  { name: "Krrish Patil", role: "Social Media Marketing / SEO" },
  { name: "Arun Sardesai", role: "Video Editor" },
  { name: "Mahesh Bhonde", role: "Senior Graphic & UI/UX Designer" },
  { name: "Samata K", role: "Content Writer" },
  { name: "Shruti Parte", role: "Performance Marketing Lead" },
];

export default function About() {
  return (
    <main data-testid="about-page" className="bg-white">
      <section className="pt-40 pb-20 grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeIn>
              <SectionLabel>About us</SectionLabel>
            </FadeIn>
            <h1 className="mt-6 font-syne font-extrabold tracking-tighter leading-[0.95] text-[13vw] sm:text-7xl lg:text-8xl text-bm-ink">
              <MaskedLine delay={0.1}>THE AGENCY</MaskedLine>
              <MaskedLine delay={0.24}>
                THAT <span className="text-bm-blue">NEVER</span>
              </MaskedLine>
              <MaskedLine delay={0.38}>
                <span className="text-stroke-blue">SLEEPS.</span>
              </MaskedLine>
            </h1>
            <FadeIn delay={0.5} className="mt-8 max-w-xl">
              <p className="font-manrope text-base sm:text-lg leading-relaxed text-bm-gray">
                Brand Mitra 24 was born from a simple frustration: agencies that
                clock out at 6pm while the internet keeps scrolling. We built a
                crew of strategists, designers, editors and media buyers who
                treat "around the clock" as a promise, not a tagline.
              </p>
              <p className="mt-4 font-manrope text-base sm:text-lg leading-relaxed text-bm-gray">
                Today we run eleven services under one roof — from SEO and paid
                media to AI video, photography and full-scale event coverage —
                for brands across India and beyond.
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={0.3} className="lg:col-span-5">
            <div className="relative">
              <div className="overflow-hidden rounded-xl border border-bm-line">
                <img
                  src={IMG.office}
                  alt="Brand Mitra 24 team at work"
                  className="h-80 sm:h-[28rem] w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 rounded-xl bg-bm-blue px-6 py-4 shadow-xl">
                <p className="font-syne text-3xl font-extrabold text-white">24/7</p>
                <p className="font-manrope text-xs font-semibold text-white/80">always on duty</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <EditorialMarquee dark={false} />

      <section data-testid="about-values" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn className="mb-12">
            <SectionLabel>What we live by</SectionLabel>
            <h2 className="mt-4 font-syne text-4xl sm:text-5xl font-bold tracking-tight text-bm-ink">
              Values on the wall — and in the work.
            </h2>
          </FadeIn>
          <div>
            {VALUES.map(([num, title, desc], i) => (
              <FadeIn key={num} delay={i * 0.06}>
                <div
                  data-testid={`value-row-${i}`}
                  className={`group grid grid-cols-12 items-center gap-4 border-t border-bm-line py-8 transition-colors duration-300 hover:bg-bm-surface ${
                    i === VALUES.length - 1 ? "border-b" : ""
                  }`}
                >
                  <span className="col-span-2 sm:col-span-1 font-syne text-sm font-bold text-bm-blue">
                    {num}
                  </span>
                  <h3 className="col-span-10 sm:col-span-5 font-syne text-2xl sm:text-3xl font-bold tracking-tight text-bm-ink transition-transform duration-300 group-hover:translate-x-2">
                    {title}
                  </h3>
                  <p className="col-span-10 col-start-3 sm:col-span-6 sm:col-start-auto font-manrope text-sm text-bm-gray">
                    {desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-team" className="bg-bm-surface py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn className="mb-14">
            <SectionLabel>The crew</SectionLabel>
            <h2 className="mt-4 font-syne text-4xl sm:text-5xl font-bold tracking-tight text-bm-ink">
              Small team. Massive output.
            </h2>
          </FadeIn>
          <div>
            {TEAM.map((m, i) => (
              <FadeIn key={m.name} delay={i * 0.06}>
                <div
                  data-testid={`team-member-${i}`}
                  className={`group grid grid-cols-12 items-center gap-4 border-t border-bm-line bg-white py-7 transition-colors duration-300 hover:bg-bm-surface ${
                    i === TEAM.length - 1 ? "border-b" : ""
                  }`}
                >
                  <span className="col-span-2 sm:col-span-1 font-syne text-sm font-bold text-bm-blue">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="col-span-10 sm:col-span-5 font-syne text-2xl sm:text-3xl font-bold tracking-tight text-bm-ink transition-transform duration-300 group-hover:translate-x-2">
                    {m.name}
                  </h3>
                  <p className="col-span-10 col-start-3 sm:col-span-6 sm:col-start-auto font-manrope text-sm font-semibold text-bm-gray">
                    {m.role}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bm-navy py-20 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <FadeIn key={s.label} delay={i * 0.1}>
              <div className="border-l-2 border-bm-blue pl-6">
                <p className="font-syne text-5xl font-extrabold tracking-tighter text-white">{s.value}</p>
                <p className="mt-2 font-manrope text-sm text-white/60">{s.label}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>
    </main>
  );
}
